<script setup>
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { ArrowLeft, ArrowRight } from "@element-plus/icons-vue";
import { useLayoutStore } from "@/stores/layout";
import { useUserStore } from "@/stores/user";

const route = useRoute();
const router = useRouter();
const layoutStore = useLayoutStore();
const userStore = useUserStore();

const implementedRoutes = new Set([
  "home",
  "classTab",
  "releaseTime",
  "autonomousCancellation",
  "classDetail",
  "trainingCenter",
  "trainingKit",
  "serviceFee",
  "SOP",
  "complaintList",
  "comments",
  "myFeedback",
  "referrals",
]);

const visibleMenus = computed(() =>
  layoutStore.mainMenus.filter((m) => {
    if (m.type !== 0 || !m.isShow) return false;
    if (m.name === "Service Fee" && userStore.isFranchiseeUser) return false;
    return true;
  }),
);

const isMini = computed(() => layoutStore.sidebarCollapsed);

function menuPath(menu) {
  return menu.routeName === "home" ? "/home" : `/${menu.routeName}`;
}

function isActive(menu) {
  const path = menuPath(menu);
  return route.path === path || route.path.startsWith(`${path}/`);
}

function onMenuClick(menu) {
  const path = menuPath(menu);
  const name = menu.routeName;
  if (!implementedRoutes.has(name)) {
    ElMessage.info(`${menu.name} — coming in Phase 1+`);
    return;
  }
  router.push(path);
}
</script>

<template>
  <aside class="app-sidebar my-navigation" :class="{ 'is-mini': isMini }">
    <nav class="app-sidebar__nav">
      <el-tooltip
        v-for="menu in visibleMenus"
        :key="menu.name"
        :content="menu.name"
        placement="right"
        :disabled="!isMini"
        :show-after="120"
        :hide-after="0"
        effect="dark"
        popper-class="siderbar-tooltip"
      >
        <button
          type="button"
          class="menu-item"
          :class="{ 'is-active': isActive(menu) }"
          @click="onMenuClick(menu)"
        >
          <span class="menu-item__icon" :class="menu.filterName">
            <i class="sidebar-icon" />
            <i
              v-if="menu.routeName === 'myFeedback' && userStore.feedBackMsg"
              class="feedback-unread"
            />
          </span>
          <span v-show="!isMini" class="menu-item__label">{{ menu.name }}</span>
        </button>
      </el-tooltip>
    </nav>

    <div class="siderbar-btn-card" :class="{ mini: isMini }">
      <button
        type="button"
        class="sidebar-btn"
        @click="layoutStore.toggleSidebar"
      >
        <el-icon>
          <ArrowRight v-if="isMini" />
          <ArrowLeft v-else />
        </el-icon>
      </button>
    </div>
  </aside>
</template>

<style scoped lang="scss">
.my-navigation {
  width: $layout-sidebar-width;
  position: fixed;
  top: $layout-header-height;
  left: 0;
  bottom: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: var(--app-sidebar-bg, #fff);
  box-shadow: var(--app-shadow, 2px 0 10px rgba(125, 162, 255, 0.19));
  transition:
    width 0.2s ease,
    background-color 0.2s ease;

  &.is-mini {
    width: $layout-sidebar-mini-width;
  }
}

.app-sidebar__nav {
  padding-top: 32px;
  overflow-y: auto;
  overflow-x: hidden;
}

.menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  height: 60px;
  padding: 0 26px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--app-text, $blue-title);
  font-family: inherit;
  font-size: 14px;
  text-align: left;
  gap: 12px;

  &:hover,
  &.is-active {
    background: var(--app-hover, #e9f4ff);
  }

  &__icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    position: relative;
  }

  &__label {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.feedback-unread {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: red;
}

.is-mini .menu-item {
  justify-content: center;
  padding: 0;
}

.sidebar-icon {
  display: block;
  width: 20px;
  height: 20px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.menu-item__icon.Home .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/dashboard.png");
}
.menu-item__icon.sop .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/sop.png");
}
.menu-item__icon.ClassTable .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/class.png");
}
.menu-item__icon.TrainingCenter .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/training.png");
}
.menu-item__icon.ServiceFee .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/fee.png");
}
.menu-item__icon.StudentComment .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/comment.png");
}
.menu-item__icon.ComplaintList .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/complaint.png");
}
.menu-item__icon.myFeedback .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/feedback.png");
}
.menu-item__icon.MyReferrals .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/referrals.png");
}

.menu-item.is-active .menu-item__icon.Home .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/dashboard-cur.png");
}
.menu-item.is-active .menu-item__icon.sop .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/sop-cur.png");
}
.menu-item.is-active .menu-item__icon.ClassTable .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/class-cur.png");
}
.menu-item.is-active .menu-item__icon.TrainingCenter .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/training-cur.png");
}
.menu-item.is-active .menu-item__icon.ServiceFee .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/fee-cur.png");
}
.menu-item.is-active .menu-item__icon.StudentComment .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/comment-cur.png");
}
.menu-item.is-active .menu-item__icon.ComplaintList .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/complaint-cur.png");
}
.menu-item.is-active .menu-item__icon.myFeedback .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/feedback-cur.png");
}
.menu-item.is-active .menu-item__icon.MyReferrals .sidebar-icon {
  background-image: url("@/assets/img/common/sidebar/referrals-cur.png");
}

.siderbar-btn-card {
  height: 40px;
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 23px 12px;
  flex-shrink: 0;

  &.mini {
    justify-content: center;
    padding-right: 0;
  }
}

.sidebar-btn {
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: rgba(62, 103, 251, 0.2);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .el-icon {
    font-size: 14px;
  }

  &:hover {
    background: rgba(62, 103, 251, 0.35);
  }
}
</style>

<style lang="scss">
/* 折叠菜单 tooltip — 对齐旧站 siderbar-tooltip / 截图2 */
.siderbar-tooltip.el-popper {
  --el-text-color-primary: #fff;
  background: rgba(176, 179, 200, 0.92) !important;
  border: none !important;
  border-radius: 4px !important;
  box-shadow: none !important;
  padding: 6px 12px !important;
  min-width: auto !important;
  font-family: Poppins, "Helvetica Neue", Arial, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  color: #fff !important;

  .el-popper__arrow::before {
    background: rgba(176, 179, 200, 0.92) !important;
    border: none !important;
  }
}
</style>
